import { useEffect, useRef, useState } from 'react';
import { loadIncomeForUser, sumMonth } from '../../loaders/cockpitLoaders';
import logger from '../../utils/logger';

/**
 * `hooks/invoice/useUsageLimitValueFraming` (M-UX-G4).
 *
 * NICHT Teil der ursprünglichen Datei-Liste des Arbeitspakets (nur
 * `config/freemium.ts` + `components/usage/InvoiceUsageMeter.tsx` waren als
 * NEU benannt) — bewusste Abweichung, dokumentiert in
 * `changelog-ux-g4-usage-meter.md`: die 403-Value-Framing-Botschaft
 * ("Sie haben diesen Monat bereits X € fakturiert…") braucht denselben
 * Firestore-Fetch + dieselbe `sumMonth`-Aggregation an 3 Call-Sites
 * (InvoiceDashboard/XRechnungDashboard/MobileInvoiceDashboard) — ein
 * gemeinsamer Hook verhindert eine 3-fache Kopie (golden-rules.md
 * "PATTERN: Shared Mapper-Funktionen ... kein Copy-Paste").
 *
 * Lädt den laufenden Monatsumsatz NUR dann (fetch-on-demand statt bei jedem
 * Dashboard-Aufruf), wenn `active` wahr ist — i. d. R. `showUsageLimitPromo`,
 * also exakt der 403-Limit-Moment. Fail-closed: jeder Fehler ODER ein
 * Monatsumsatz von `0` (oder kleiner) ⇒ `revenueCents: null`; der Aufrufer
 * fällt dann auf die generische `usageLimitPromo.message` zurück statt eine
 * unplausible "0,00 € fakturiert"-Botschaft zu zeigen (Muster
 * `cockpitLoaders.ts` "Kachel zeigt — bei Fehler").
 *
 * @module hooks/invoice/useUsageLimitValueFraming
 */
export interface UsageLimitValueFraming {
  /** Monatsumsatz in Integer-Cent, oder `null` solange nicht geladen / Fehler / nicht plausibel (`<= 0`). */
  revenueCents: number | null;
  /**
   * `true`, sobald der Fetch für den aktuellen `scopeUid` final ist (Erfolg,
   * Fehler ODER bereits zuvor für denselben Nutzer geladen/gecacht) — d. h.
   * `revenueCents` ändert sich für diesen Scope nicht mehr. `false` während
   * der Fetch noch läuft bzw. solange `active`/`scopeUid` fehlen.
   *
   * Existiert primär, damit Aufrufer (M-N3 `view_promotion`) auf die
   * *finale* `revenueCents`-Auflösung warten können, statt ein Event mit
   * einem noch-nicht-aufgelösten Zwischenstand zu feuern — verhindert ein
   * Doppel-Fire (erst `generic`, dann Re-Fire nach Auflösung `value_framed`).
   */
  resolved: boolean;
}

/**
 * @param scopeUid - i. d. R. `currentUser?.uid` (diese Dashboards nutzen KEIN
 *   `effectiveScopeUid`-Multi-Tenant-Scoping — konsistent mit dem
 *   Bestandscode dieser 3 Dateien, die ebenfalls direkt `currentUser` nutzen).
 *   `null`/`undefined` ⇒ kein Fetch.
 * @param active - i. d. R. `showUsageLimitPromo`. Wechselt es von `false` auf
 *   `true`, wird für den aktuellen `scopeUid` genau einmal geladen (kein
 *   Re-Fetch bei jedem Re-Render, solange derselbe Nutzer aktiv bleibt).
 */
export function useUsageLimitValueFraming(
  scopeUid: string | null | undefined,
  active: boolean,
): UsageLimitValueFraming {
  const [revenueCents, setRevenueCents] = useState<number | null>(null);
  const [resolved, setResolved] = useState(false);
  const fetchedForRef = useRef<string | null>(null);

  useEffect(() => {
    if (!active || !scopeUid) return;
    // Bereits für diesen Nutzer geladen (oder in Arbeit) — `resolved` trägt
    // den Endstand des vorherigen Laufs bereits korrekt fort, kein Re-Fetch.
    if (fetchedForRef.current === scopeUid) return;

    let cancelled = false;
    fetchedForRef.current = scopeUid;
    setResolved(false);

    (async () => {
      try {
        const entries = await loadIncomeForUser(scopeUid);
        const todayISO = new Date().toISOString().slice(0, 10);
        const totalCents = sumMonth(entries, todayISO);
        if (!cancelled) {
          setRevenueCents(totalCents > 0 ? totalCents : null);
          setResolved(true);
        }
      } catch (error) {
        logger.debug('[useUsageLimitValueFraming] Monatsumsatz konnte nicht geladen werden:', error);
        if (!cancelled) {
          setRevenueCents(null);
          setResolved(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [scopeUid, active]);

  return { revenueCents, resolved };
}

export default useUsageLimitValueFraming;
